/**
 * Inline-editable cells for the issue DataTable.
 * Click to open a dropdown, select to save immediately.
 */

import { useState } from 'react';
import { StatusBadge } from '@/components/StatusBadge.js';
import { PriorityIcon } from '@/components/PriorityIcon.js';
import { Avatar } from '@/components/Avatar.js';
import { useIssueTransitions, useInlineTransition, useUpdateIssueField } from '@/hooks/useIssues.js';
import type { JiraIssueSummary } from '@shared/types.js';

// ── Dropdown Shell ──────────────────────────────────────────────────

interface DropdownProps {
  open: boolean;
  onClose: () => void;
  /** Which edge of the trigger the menu lines up with */
  align?: 'left' | 'right';
  children: React.ReactNode;
}

/**
 * Menu plus a transparent full-screen backdrop that closes it on any outside
 * click — no document listeners to leak, and clicking the trigger again (which
 * sits under the backdrop) just closes it.
 */
function Dropdown({ open, onClose, align = 'left', children }: DropdownProps) {
  if (!open) return null;

  return (
    <>
      <button type="button" className="fixed inset-0 z-20 cursor-default" aria-label="Close menu" onClick={onClose} tabIndex={-1} />
      <div
        className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} top-full z-30 mt-1 max-h-60 min-w-[180px] overflow-y-auto rounded-md border bg-white py-1 text-left shadow-lg`}
        onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
      >
        {children}
      </div>
    </>
  );
}

// ── Status Cell ─────────────────────────────────────────────────────

interface StatusCellProps {
  issue: Pick<JiraIssueSummary, 'key' | 'status'>;
  menuAlign?: 'left' | 'right';
}

export function EditableStatusCell({ issue, menuAlign }: StatusCellProps) {
  const [open, setOpen] = useState(false);
  const { data: transitions, isLoading } = useIssueTransitions(issue.key, open);
  const transitionMutation = useInlineTransition();

  function handleSelect(transitionId: string) {
    setOpen(false);
    transitionMutation.mutate({ issueKey: issue.key, transitionId });
  }

  return (
    <div className="relative flex items-center">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="cursor-pointer rounded transition-colors hover:ring-2 hover:ring-blue-200"
        title="Change status"
      >
        <StatusBadge status={issue.status} />
      </button>
      <Dropdown open={open} onClose={() => setOpen(false)} align={menuAlign}>
        {isLoading ? (
          <div className="px-3 py-2 text-xs text-gray-400">Loading...</div>
        ) : (
          transitions?.map((t) => (
            <button
              key={t.id}
              onClick={() => handleSelect(t.id)}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-gray-50"
            >
              <span className="text-gray-700">{t.to?.name || t.name}</span>
            </button>
          ))
        )}
      </Dropdown>
    </div>
  );
}

// ── Priority Cell ───────────────────────────────────────────────────

interface PriorityCellProps {
  issue: JiraIssueSummary;
  priorities: Array<{ id: string; name: string }>;
}

export function EditablePriorityCell({ issue, priorities }: PriorityCellProps) {
  const [open, setOpen] = useState(false);
  const updateMutation = useUpdateIssueField();

  function handleSelect(priorityName: string) {
    setOpen(false);
    // PUT /api/issues/:key takes the priority name (the server wraps it for Jira)
    updateMutation.mutate({
      issueKey: issue.key,
      fields: { priority: priorityName },
    });
  }

  return (
    <div className="relative flex items-center justify-center">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="cursor-pointer rounded transition-colors hover:ring-2 hover:ring-blue-200"
        title="Change priority"
      >
        <PriorityIcon name={issue.priority.name} iconUrl={issue.priority.iconUrl} />
      </button>
      <Dropdown open={open} onClose={() => setOpen(false)}>
        {priorities.map((p) => (
          <button
            key={p.id}
            onClick={() => handleSelect(p.name)}
            className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-gray-50 ${
              p.name === issue.priority.name ? 'bg-blue-50 font-medium text-blue-700' : 'text-gray-700'
            }`}
          >
            {p.name}
          </button>
        ))}
      </Dropdown>
    </div>
  );
}

// ── Assignee Cell ───────────────────────────────────────────────────

interface AssigneeCellProps {
  issue: Pick<JiraIssueSummary, 'key' | 'assignee'>;
  users: Array<{ accountId: string; displayName: string; avatarUrl: string | null }>;
}

export function EditableAssigneeCell({ issue, users }: AssigneeCellProps) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const updateMutation = useUpdateIssueField();

  function handleSelect(accountId: string | null) {
    setOpen(false);
    setFilter('');
    updateMutation.mutate({
      issueKey: issue.key,
      // PUT /api/issues/:key takes the accountId string (or null to unassign)
      fields: { assignee: accountId },
    });
  }

  const filtered = filter.trim()
    ? users.filter((u) => u.displayName.toLowerCase().includes(filter.toLowerCase()))
    : users;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full cursor-pointer items-center gap-1 rounded px-1 py-0.5 text-left transition-colors hover:ring-2 hover:ring-blue-200"
        title="Change assignee"
      >
        {issue.assignee ? (
          <>
            <Avatar name={issue.assignee.displayName} avatarUrl={issue.assignee.avatarUrl} />
            <span className="truncate text-gray-700">{issue.assignee.displayName}</span>
          </>
        ) : (
          <span className="text-gray-400">Unassigned</span>
        )}
      </button>
      <Dropdown open={open} onClose={() => { setOpen(false); setFilter(''); }}>
        <div className="border-b px-2 py-1">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search..."
            className="w-full rounded border-none px-1 py-0.5 text-sm outline-none"
            autoFocus
          />
        </div>
        <button
          onClick={() => handleSelect(null)}
          className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-gray-50 ${
            !issue.assignee ? 'bg-blue-50 font-medium text-blue-700' : 'text-gray-400'
          }`}
        >
          Unassigned
        </button>
        {filtered.map((u) => (
          <button
            key={u.accountId}
            onClick={() => handleSelect(u.accountId)}
            className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-gray-50 ${
              issue.assignee?.displayName === u.displayName ? 'bg-blue-50 font-medium text-blue-700' : 'text-gray-700'
            }`}
          >
            <Avatar name={u.displayName} avatarUrl={u.avatarUrl} size="sm" />
            <span className="truncate">{u.displayName}</span>
          </button>
        ))}
      </Dropdown>
    </div>
  );
}
